import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  XMarkIcon,
  MagnifyingGlassIcon,
  UserIcon,
  PhoneIcon
} from '@heroicons/react/24/outline';
import { createNewConversation } from '../../store/thunks/chatThunk';
import { searchMobileApi } from '../../store/thunks/searchApiThunk';

const NewConversationModal = ({ onClose, onConversationCreated }) => {
  const dispatch = useDispatch();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Debounced search
    const timeoutId = setTimeout(async () => {
      if (searchQuery.trim()) {
        setIsSearching(true);
        try {
          const result = await dispatch(searchMobileApi(searchQuery.trim())).unwrap();
          
          // Mobile search API returns clients directly, already filtered and optimized for SMS
          const clients = Array.isArray(result) ? result : [];
          
          console.log('[NewConversation] Mobile search results:', { clients: clients.length });
          setSearchResults(clients);
        } catch (err) {
          console.error('Search error:', err);
          setSearchResults([]);
        } finally {
          setIsSearching(false);
        }
      } else {
        setSearchResults([]);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, dispatch]);

  const handleCreateConversation = async () => {
    if (!selectedClient) return;

    setIsLoading(true);
    setError(null);

    try {
      // Get phone number - handle both object and string formats
      let phoneNumber = null;
      if (selectedClient.phoneNumbers && selectedClient.phoneNumbers.length > 0) {
        const firstPhone = selectedClient.phoneNumbers[0];
        phoneNumber = typeof firstPhone === 'object' ? firstPhone.number : firstPhone;
      }

      const result = await dispatch(createNewConversation({
        clientId: selectedClient._id,
        phoneNumber: phoneNumber
      })).unwrap();

      onConversationCreated(result.conversationId);
    } catch (err) {
      setError(err.message || 'Failed to create conversation');
    } finally {
      setIsLoading(false);
    }
  };

  const getClientDisplayInfo = (client) => {
    const name = `${client.firstName || ''} ${client.lastName || ''}`.trim();
    
    console.log(`[NewConversation] Processing client ${name}:`, {
      hasPreferredNumber: !!client.preferredNumber,
      preferredNumber: client.preferredNumber,
      smsCapable: client.smsCapable,
      hasMobile: client.hasMobile,
      phoneNumbers: client.phoneNumbers
    });
    
    let phone = 'No phone number';
    let phoneType = '';
    let smsReady = false;
    
    // Use preferredNumber from mobile search if available
    if (client.preferredNumber) {
      phone = client.preferredNumber.number;
      phoneType = client.preferredNumber.type;
      smsReady = client.smsCapable;
      console.log(`[NewConversation] Using preferred number for ${name}:`, phone);
    } else if (client.phoneNumbers && client.phoneNumbers.length > 0) {
      // Fallback: Find mobile number ourselves if mobile search didn't process it
      const mobileWithConsent = client.phoneNumbers.find(
        phone => phone.type === 'Mobile' && phone.smsConsent
      );
      
      if (mobileWithConsent) {
        phone = mobileWithConsent.number;
        phoneType = mobileWithConsent.type;
        smsReady = true;
        console.log(`[NewConversation] Using mobile with consent for ${name}:`, phone);
      } else {
        const anyMobile = client.phoneNumbers.find(
          phone => phone.type === 'Mobile'
        );
        
        if (anyMobile) {
          phone = anyMobile.number;
          phoneType = anyMobile.type;
          smsReady = true;
          console.log(`[NewConversation] Using any mobile for ${name}:`, phone);
        } else {
          const firstPhone = client.phoneNumbers[0];
          if (typeof firstPhone === 'object' && firstPhone.number) {
            phone = firstPhone.number;
            phoneType = firstPhone.type || '';
          } else if (typeof firstPhone === 'string') {
            phone = firstPhone;
          }
          console.log(`[NewConversation] Using first phone for ${name}:`, phone);
        }
      }
    }
    
    return { 
      name: name || 'Unnamed Client', 
      phone, 
      phoneType,
      smsReady,
      hasMobile: client.hasMobile
    };
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">New Conversation</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Search Input */}
          <div className="relative mb-4">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search clients by name or phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Selected Client */}
          {selectedClient && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                  <UserIcon className="h-4 w-4 text-white" />
                </div>
                <div className="flex-1">
                  <div className="font-medium text-blue-900">
                    {getClientDisplayInfo(selectedClient).name}
                  </div>
                  <div className="text-sm text-blue-600 flex items-center gap-1">
                    <PhoneIcon className="h-3 w-3" />
                    {getClientDisplayInfo(selectedClient).phone}
                    {getClientDisplayInfo(selectedClient).phoneType && (
                      <span className="text-xs bg-blue-100 text-blue-800 px-1 rounded">
                        {getClientDisplayInfo(selectedClient).phoneType}
                      </span>
                    )}
                    {getClientDisplayInfo(selectedClient).smsReady && (
                      <span className="text-xs bg-green-100 text-green-800 px-1 rounded">
                        SMS Ready
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => setSelectedClient(null)}
                  className="text-blue-400 hover:text-blue-600"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          {/* Client List */}
          {!selectedClient && (
            <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
              {isSearching ? (
                <div className="p-4 text-center text-gray-500">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto mb-2"></div>
                  Searching clients...
                </div>
              ) : searchResults.length === 0 && searchQuery ? (
                <div className="p-4 text-center text-gray-500">
                  No clients found for "{searchQuery}"
                </div>
              ) : searchResults.length === 0 && !searchQuery ? (
                <div className="p-4 text-center text-gray-500">
                  Start typing to search for clients
                </div>
              ) : (
                searchResults.map((client) => {
                  const { name, phone, phoneType, smsReady, hasMobile } = getClientDisplayInfo(client);
                  return (
                    <button
                      key={client._id}
                      onClick={() => setSelectedClient(client)}
                      className="w-full p-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                          smsReady ? 'bg-green-200' : hasMobile ? 'bg-blue-200' : 'bg-gray-200'
                        }`}>
                          <UserIcon className={`h-4 w-4 ${
                            smsReady ? 'text-green-600' : hasMobile ? 'text-blue-600' : 'text-gray-500'
                          }`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900 truncate">
                            {name}
                          </div>
                          <div className="text-sm text-gray-500 flex items-center gap-1 flex-wrap">
                            <PhoneIcon className="h-3 w-3" />
                            {phone}
                            {phoneType && (
                              <span className="text-xs bg-gray-100 text-gray-700 px-1 rounded">
                                {phoneType}
                              </span>
                            )}
                            {smsReady && (
                              <span className="text-xs bg-green-100 text-green-800 px-1 rounded">
                                SMS Ready
                              </span>
                            )}
                            {!smsReady && hasMobile && (
                              <span className="text-xs bg-blue-100 text-blue-800 px-1 rounded">
                                Mobile
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Development Mode Notice */}
          {process.env.NODE_ENV === 'development' && selectedClient && (
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-700 text-sm">
              <strong>Development Mode:</strong> SMS will only be sent to +1-416-669-6505
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleCreateConversation}
            disabled={!selectedClient || isLoading}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isLoading ? 'Creating...' : 'Start Conversation'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewConversationModal;